import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import { movingTipsArticles } from '../../mocks/movingTipsArticles';

export default function MovingTips() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Packing Tips': 'bg-yellow-400 text-gray-900',
      'Moving Checklist': 'bg-blue-500 text-white',
      'Family Moving': 'bg-green-500 text-white',
      'Eco Tips': 'bg-emerald-500 text-white',
      'Planning': 'bg-purple-500 text-white',
      'Organization': 'bg-orange-500 text-white',
      'Cost Analysis': 'bg-red-500 text-white'
    };
    return colors[category] || 'bg-gray-500 text-white';
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/0cdc8a6b92ed12030230ca2c206db970.png"
              alt="BoxFreeMove"
              className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12' : 'h-28'}`}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400">Home</Link>
            <a href="/#how-it-works" onClick={() => handleNavClick('#how-it-works')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">How It Works</a>
            <a href="/#pricing" onClick={() => handleNavClick('#pricing')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">Pricing</a>
            <Link to="/moving-tips" className="text-sm font-medium text-yellow-500 transition-colors hover:text-yellow-400">Moving Tips</Link>
            <a href="/#faq" onClick={() => handleNavClick('#faq')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">FAQ</a>
            <a href="/#reserve" onClick={() => handleNavClick('#reserve')} className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
              Reserve Now
            </a>
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile dropdown menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <a
              href="/#how-it-works"
              onClick={() => handleNavClick('#how-it-works')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              How It Works
            </a>
            <a
              href="/#pricing"
              onClick={() => handleNavClick('#pricing')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Pricing
            </a>
            <Link
              to="/moving-tips"
              onClick={() => setMenuOpen(false)}
              className="text-yellow-500 font-medium text-sm hover:text-yellow-400 transition-colors cursor-pointer"
            >
              Moving Tips
            </Link>
            <a
              href="/#faq"
              onClick={() => handleNavClick('#faq')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              FAQ
            </a>
            <a
              href="/#reserve"
              onClick={() => handleNavClick('#reserve')}
              className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer w-full text-center"
            >
              Reserve Now
            </a>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">Moving Tips &amp; Guides</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Expert advice to make your move smoother, faster, and stress-free. From packing strategies to settling in.
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="flex-1 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {movingTipsArticles.map((article) => (
              <Link
                key={article.id}
                to={`/moving-tips/article?id=${article.id}`}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative w-full h-64 bg-gray-100">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getCategoryColor(article.category)}`}>
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{article.readTime}</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3 leading-tight hover:text-yellow-500 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-yellow-500 font-semibold text-sm hover:text-yellow-600 transition-colors whitespace-nowrap cursor-pointer">
                    Read More
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}